/**
 * Contact Section Component
 * Migrated to TypeScript
 */

import React, { useEffect, useRef, useState } from 'react';
import { FaEnvelope, FaMapMarkerAlt, FaPhone, FaLinkedin, FaGithub } from 'react-icons/fa';
import { personalInfo } from '../../data/personal';
import { socialLinks } from '../../data/social';
import { useColorScheme } from '../../contexts/ColorSchemeContext';

const Contact: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { getColorClasses } = useColorScheme();
  const colors = getColorClasses();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="contact" ref={sectionRef} className="py-20">
      <div className="container mx-auto px-4">
        <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="accent-gradient-text">
                Get In Touch
              </span>
            </h2>
            <p className={`text-xl ${colors.textSecondary} max-w-3xl mx-auto`}>
              Let's connect and discuss opportunities
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Info */}
            <div className="space-y-8">
              <div className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border ${colors.borderColor}`}>
                <h3 className={`text-2xl font-bold mb-6 ${colors.textPrimary} flex items-center`}>
                  <FaEnvelope className="w-6 h-6 mr-3" style={{ color: '#EA4335' }} />
                  Get In Touch & Connect
                </h3>
                <p className={`${colors.textSecondary} mb-6 leading-relaxed`}>
                  I'm always interested in new opportunities and collaborations. 
                  Feel free to reach out if you'd like to discuss potential projects or just say hello!
                </p>
                
                <div className="grid grid-cols-2 gap-4">
                  {/* Contact Cards */}
                  <div className="flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-yellow-500 transition-colors">
                    <FaEnvelope className="w-5 h-5" style={{ color: '#EA4335' }} />
                    <div>
                      <h4 className={`font-semibold ${colors.textPrimary} text-sm`}>Email</h4>
                      <a 
                        href={`mailto:${personalInfo.email}`}
                        className={`${colors.textSecondary} hover:${colors.text} transition-colors text-sm`}
                      >
                        {personalInfo.email}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-yellow-500 transition-colors">
                    <FaPhone className="w-5 h-5" style={{ color: '#25D366' }} />
                    <div>
                      <h4 className={`font-semibold ${colors.textPrimary} text-sm`}>Phone</h4>
                      <a 
                        href={`tel:${personalInfo.phone}`}
                        className={`${colors.textSecondary} hover:${colors.text} transition-colors text-sm`}
                      >
                        {personalInfo.phone}
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-yellow-500 transition-colors">
                    <FaMapMarkerAlt className="w-5 h-5" style={{ color: '#4285F4' }} />
                    <div>
                      <h4 className={`font-semibold ${colors.textPrimary} text-sm`}>Location</h4>
                      <p className={`${colors.textSecondary} text-sm`}>{personalInfo.location}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-yellow-500 transition-colors">
                    <FaLinkedin className="w-5 h-5" style={{ color: '#0A66C2' }} />
                    <div>
                      <h4 className={`font-semibold ${colors.textPrimary} text-sm`}>LinkedIn</h4>
                      <a 
                        href={socialLinks.find(link => link.name === 'LinkedIn')?.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textSecondary} hover:${colors.text} transition-colors text-sm`}
                      >
                        ashraf8ila
                      </a>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3 p-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:border-yellow-500 transition-colors">
                    <FaGithub className="w-5 h-5 text-gray-800 dark:text-gray-200" />
                    <div>
                      <h4 className={`font-semibold ${colors.textPrimary} text-sm`}>GitHub</h4>
                      <a 
                        href={socialLinks.find(link => link.name === 'GitHub')?.url || '#'}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${colors.textSecondary} hover:${colors.text} transition-colors text-sm`}
                      >
                        ashraf8ila
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`${colors.cardBackground} rounded-2xl p-8 shadow-lg border ${colors.borderColor}`}>
              <h3 className={`text-2xl font-bold mb-6 ${colors.textPrimary} flex items-center`}>
                <FaEnvelope className="w-6 h-6 mr-3 text-accent-500" />
                Send a Message
              </h3>
              
              <form className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                      Name
                    </label>
                    <input
                      type="text"
                      className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 ${colors.cardBackground} ${colors.textPrimary} focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-300`}
                      placeholder="Your Name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                      Email
                    </label>
                    <input
                      type="email"
                      className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 ${colors.cardBackground} ${colors.textPrimary} focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-300`}
                      placeholder="your.email@example.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                    Subject
                  </label>
                  <input
                    type="text"
                    className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 ${colors.cardBackground} ${colors.textPrimary} focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-300`}
                    placeholder="What's this about?"
                  />
                </div>
                
                <div>
                  <label className={`block text-sm font-medium ${colors.textPrimary} mb-2`}>
                    Message
                  </label>
                  <textarea
                    rows={4}
                    className={`w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 ${colors.cardBackground} ${colors.textPrimary} focus:ring-2 focus:ring-accent-500 focus:border-transparent transition-all duration-300 resize-none`}
                    placeholder="Tell me about your project or just say hello!"
                  />
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-gradient-to-r accent-gradient-bg text-white font-semibold py-3 px-6 rounded-lg hover:opacity-90 transition-opacity duration-300 transform hover:scale-105"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;

